import cx from 'classnames'
import PubSub from 'pubsub-js'
import React, { useEffect, useRef, useState } from 'react'

import { getUrlByDomain } from '@js/common/getUrlByDomain'
import useFocusWithinDetection from '@js/common/hooks/useFocusWithinDetection'
import { UA } from '@js/common/UA'

import CTA from '@f/elements/CTA/CTA'
import Icon from '@f/elements/Icon/Icon'

import { useClientOnly } from '@f-fertighaus/components/ClientOnly/ClientOnly'

import { MenuItemType, usePageHeader } from '../PageHeaderSSR'
import { handleMenuItemClickType } from '../PageHeaderStandard'

interface UserMenuProps {
  currentActiveMenu: MenuItemType | null
  handleMenuItemClick: handleMenuItemClickType
  closeAllMenus: () => void
  keepActiveMenuOpen: boolean
}

export const UserMenu = (props: UserMenuProps) => {
  const [isLoggedIn, setLoggedIn] = useState(UA.isLoggedIn())
  const { domain } = usePageHeader()
  const isMounted = useClientOnly()
  const getUrl = getUrlByDomain(domain)
  const containerRef = useRef(null)

  useEffect(() => {
    const token = PubSub.subscribe('USER_LOGGED_IN', () => {
      setLoggedIn(true)
    })

    return () => {
      PubSub.unsubscribe(token)
    }
  }, [])

  useFocusWithinDetection({
    containerRef,
    onFocusOut: props.closeAllMenus,
    onFocusIn: () => {
      props.handleMenuItemClick('user', false)
    }
  })

  const isUserMenuOpen =
    (props.currentActiveMenu && /user/.test(props.currentActiveMenu)) ||
    (props.keepActiveMenuOpen && /user/.test(props.currentActiveMenu))

  return (
    <div
      className='[ f-d-inbl f-pos-rel ] PageHeader-menu-wrapper'
      ref={containerRef}>
      <CTA
        type='icon'
        onClick={() => {
          props.handleMenuItemClick('user', true)
        }}
        onMouseEnter={() => {
          props.handleMenuItemClick('user', false)
        }}
        className={cx(
          '[ f-p-a-010 f-m-v-010 f-hidden:s f-hidden:m ]',
          'PageHeader-button PageHeader-with-submenu PageHeader-menu-toggle',
          props.currentActiveMenu === 'user' && 'is-open'
        )}
        data-sel='btn-nav-usr'
        wtData={{
          'CTA-click': {
            label: 'user-menu',
            location: 'header'
          }
        }}>
        <Icon
          icon='user-16px'
          className={cx('PageHeader-button-icon PageHeader-icon', 'f-c-st-whi')}
        />
        <span className='[ f-c-st-inherit ] PageHeader-button-label'>
          {domain === 'fertighaus' ? 'Mein Profil' : 'Mein Konto'}
        </span>
      </CTA>

      <nav
        className={cx(
          '[ f-c-ba-whi ] PageHeader-menu PageHeader-user-dropdown PageHeader-menu__right',
          isUserMenuOpen ? 'is-active' : 'f-visually-hidden'
        )}>
        <ul className='f-!list f-d-flc f-p-a-020 f-fl-gap-020'>
          {isMounted && (
            <>
              {isLoggedIn ? (
                <>
                  <li>
                    <CTA
                      tag='a'
                      href={getUrl('userAccount')()}
                      type='text-primary'
                      className='[ f-t-mini ] PageHeader-menu-button'
                      data-sel='lnk-ua'
                      wtData={{
                        'CTA-click': {
                          location: 'header'
                        }
                      }}>
                      Zu meinem Projekt
                    </CTA>
                  </li>
                  <li>
                    <CTA
                      tag='a'
                      href={getUrl('userProfile')()}
                      type='text-primary'
                      className='[ f-t-mini ] PageHeader-menu-button'
                      data-sel='lnk-ua'
                      wtData={{
                        'CTA-click': {
                          location: 'header'
                        }
                      }}>
                      {/(massivhaus)|(bungalow)/.test(window.__DOMAIN__)
                        ? 'Mein Konto'
                        : 'Mein Profil'}
                    </CTA>
                  </li>
                  <li>
                    <CTA
                      type='text-primary'
                      className='[ f-t-mini ] PageHeader-menu-button'
                      data-sel='lnk-ua'
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        props.closeAllMenus()
                        if (isLoggedIn) {
                          e.preventDefault()
                          UA.doLogOut()
                          window.location.href = window.location.origin
                        }
                      }}
                      wtData={{
                        'CTA-click': {
                          label: 'account',
                          location: 'header'
                        }
                      }}>
                      Ausloggen
                    </CTA>
                  </li>
                </>
              ) : (
                <li>
                  <CTA
                    tag='a'
                    type='text-primary'
                    href={getUrl('userAccount')()}
                    className='[ f-t-mini ] PageHeader-menu-button'
                    data-sel='lnk-ua'
                    onClick={() => {
                      props.closeAllMenus()
                    }}
                    wtData={{
                      'CTA-click': {
                        label: 'account',
                        location: 'header'
                      }
                    }}>
                    Anmelden
                  </CTA>
                </li>
              )}
            </>
          )}
        </ul>
      </nav>
    </div>
  )
}
